import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Mail, CheckCircle, Clock, RefreshCw, Loader2, AlertCircle } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useApi } from '../../services/api'

interface EmailVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  email: string
}

const verificationSchema = yup.object({
  code: yup
    .string()
    .matches(/^\d{6}$/, 'Código deve ter exatamente 6 dígitos')
    .required('Código de verificação é obrigatório'),
})

interface VerificationFormData {
  code: string
}

const EmailVerificationModal = ({ isOpen, onClose, onSuccess, email }: EmailVerificationModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [attempts, setAttempts] = useState(0)
  const [codeSubmitted, setCodeSubmitted] = useState(false)
  const { confirmEmailVerification, resendVerificationCode } = useApi()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<VerificationFormData>({
    resolver: yupResolver(verificationSchema),
  })

  const watchedCode = watch('code')

  // Contador para reenvio de código
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Auto-submit quando código tem 6 dígitos
  useEffect(() => {
    if (watchedCode && watchedCode.length === 6 && !isLoading && !success) {
      // Adiciona um delay maior e proteção contra múltiplas chamadas
      const timer = setTimeout(() => {
        if (!isLoading && !success) {
          handleSubmit(onSubmit)()
        }
      }, 1000)
      
      return () => clearTimeout(timer)
    }
  }, [watchedCode]) // Remove isLoading e success das dependências

  const onSubmit = async (data: VerificationFormData) => {
    // Proteção contra múltiplas chamadas
    if (isLoading || success || codeSubmitted) return
    
    setIsLoading(true)
    setError(null)
    setCodeSubmitted(true)

    try {
      // Obter token do localStorage
      const token = localStorage.getItem('verification_token') || ''
      
      console.log('🔍 Dados de verificação:', {
        email,
        code: data.code,
        token: token ? `${token.substring(0, 20)}...` : 'vazio'
      })
      
      const result = await confirmEmailVerification({
        email,
        code: data.code.toString(), // Garantir que seja string
        token,
      })

      if (result.message) {
        // Limpar dados de verificação após sucesso
        localStorage.removeItem('verification_token')
        localStorage.removeItem('pending_verification')
        localStorage.removeItem('user_id')
        setSuccess(true)
        setTimeout(() => {
          onSuccess()
          reset()
          setSuccess(false)
        }, 2000)
      } else {
        setError(result.message || 'Código inválido. Tente novamente.')
        setAttempts(prev => prev + 1)
        setCodeSubmitted(false) // Reset para permitir nova tentativa
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro na verificação')
      setAttempts(prev => prev + 1)
      setCodeSubmitted(false) // Reset para permitir nova tentativa
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsResending(true)
    setError(null)

    try {
      await resendVerificationCode(email)
      setCountdown(60) // 60 segundos de espera
      setAttempts(0)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao reenviar código')
    } finally {
      setIsResending(false)
    }
  }

  const handleClose = () => {
    if (!isLoading && !success) {
      reset()
      setError(null)
      setAttempts(0)
      setCodeSubmitted(false)
      onClose()
    }
  }

  // Previne que o campo seja limpo durante o loading
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) {
      e.preventDefault()
      return
    }
  }

  // Previne que o formulário seja submetido múltiplas vezes
  const handleFormSubmit = (e: React.FormEvent) => {
    if (isLoading || success) {
      e.preventDefault()
      return
    }
  }

  if (success) {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Email Verificado!</h3>
              <p className="text-gray-600 mb-6">
                Seu email foi verificado com sucesso. Redirecionando para login...
              </p>
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
                      <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header */}
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Verificar Email</h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="p-1 md:p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50"
              >
                <X className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>

            {/* Instructions */}
            <div className="text-center mb-6 md:mb-8">
              <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">
                Enviamos um código de verificação para:
              </p>
              <p className="text-base md:text-lg font-semibold text-primary-600 break-all">
                {email}
              </p>
              <p className="text-xs md:text-sm text-gray-500 mt-2">
                Digite o código de 6 dígitos que você recebeu
              </p>
              <p className="text-xs text-blue-600 mt-2 bg-blue-50 p-2 rounded-lg">
                💡 Verifique sua caixa de entrada e pasta de spam. Se não recebeu, clique em "Reenviar código" abaixo.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" onInvalid={handleFormSubmit}>
              {/* Verification Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código de Verificação
                </label>
                <div className="relative">
                  <input
                    {...register('code')}
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    className="w-full text-center text-xl md:text-2xl font-mono tracking-widest py-3 md:py-4 px-4 md:px-6 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    onChange={handleInputChange}
                  />
                  {isLoading && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
                    </div>
                  )}
                </div>
                {errors.code && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-600"
                  >
                    {errors.code.message}
                  </motion.p>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-600 font-medium">{error}</p>
                    <p className="text-xs text-red-500 mt-1">
                      Tente reenviar o código ou verifique se digitou corretamente.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Attempts Warning */}
              {attempts >= 3 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center space-x-3"
                >
                  <Clock className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                  <p className="text-sm text-yellow-700">
                    Muitas tentativas. Aguarde um pouco antes de tentar novamente.
                  </p>
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading || attempts >= 3}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold py-2.5 md:py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm md:text-base"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Verificando...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Verificar Email</span>
                  </>
                )}
              </motion.button>
            </form>

            {/* Resend Code Section */}
            <div className="mt-6 md:mt-8 text-center">
              <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">
                Não recebeu o código?
              </p>
              
              {countdown > 0 ? (
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Reenviar em {countdown}s</span>
                </div>
              ) : (
                <motion.button
                  onClick={handleResendCode}
                  disabled={isResending}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Reenviando...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Reenviar código</span>
                    </>
                  )}
                </motion.button>
              )}
            </div>

            {/* Footer Info */}
            <div className="mt-4 md:mt-6 text-center">
              <p className="text-xs text-gray-500">
                O código expira em 10 minutos. Verifique sua caixa de spam se não receber.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default EmailVerificationModal
